import React from "react";
import { makeStyles, useTheme } from "@material-ui/core";
import { StatusType, getStatusIconColor } from "../../../constants/status";
import { getStatusLabel, getStatusStyle, getStatusTooltip, resolveThemeMode } from "../../../utils/statusUtils";
import Chip from "../Chip/Chip";
import StatusIcon from "./StatusIcon";

export interface TestStatusChipProps {
  status: StatusType;
  label?: string;
  tooltip?: string;
  showIcon?: boolean;
  excluded?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Set when the caller wraps this chip in its own Tooltip, to avoid a redundant native browser tooltip. */
  disableNativeTitle?: boolean;
}

const useStyles = makeStyles({
  root: {
    maxWidth: 200,
    fontWeight: 600,
  },
  skipped: {
    fontWeight: 400,
    "& .MuiChip-label": {
      fontWeight: 400,
    },
  },
  icon: {
    color: "inherit",
  },
  iconMatchText: {
    display: "inline-flex",
    color: "inherit",
    "& .MuiChip-icon": {
      color: "inherit",
    },
  },
});

function TestStatusChip({ status, label, tooltip, showIcon = true, excluded, className, style, disableNativeTitle }: TestStatusChipProps) {
  const classes = useStyles();
  const theme = useTheme();
  const mode = resolveThemeMode(theme.palette.type);
  const statusStyle = getStatusStyle(status, mode);
  const displayLabel = label ?? getStatusLabel(status);
  const displayTooltip = tooltip ?? getStatusTooltip(status);
  const isSkipped = status === "skipped";
  const textColor = statusStyle.color;
  const iconColor = isSkipped ? getStatusIconColor(status, mode) : textColor;

  return (
    <Chip
      className={[classes.root, isSkipped ? classes.skipped : undefined, className].filter(Boolean).join(" ")}
      size="small"
      label={displayLabel}
      title={disableNativeTitle ? undefined : displayTooltip}
      aria-label={displayLabel}
      icon={
        showIcon ? (
          isSkipped ? (
            <span className={classes.iconMatchText} style={{ color: iconColor }}>
              <StatusIcon
                status={status}
                excluded={excluded}
                fontSize="small"
                className={classes.icon}
                colored={false}
              />
            </span>
          ) : (
            <StatusIcon status={status} excluded={excluded} fontSize="small" className={classes.icon} colored={false} />
          )
        ) : undefined
      }
      style={{
        backgroundColor: statusStyle.backgroundColor,
        color: textColor,
        ...style,
      }}
    />
  );
}

export default TestStatusChip;
